import { useEffect, useState } from 'react';

import { useIdpDisclaimerQuery } from '@/entities/dosespot-idp';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Button } from '@/shared/ui/shadcn/button';
import { Checkbox } from '@/shared/ui/shadcn/checkbox';

import { WizardStepSkeleton } from './_shared/WizardStepSkeleton';
import { DS_STEP_FORM_ID } from './_shared/dsStepFormId';

export interface IdpDisclaimerStepProps {
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: Error | null;
  onReadyChange: (ready: boolean) => void;
}

export function IdpDisclaimerStep({
  onSubmit,
  isSubmitting,
  submitError,
  onReadyChange,
}: IdpDisclaimerStepProps) {
  const [hasReadDisclaimer, setHasReadDisclaimer] = useState<boolean>(false);

  const disclaimerQuery = useIdpDisclaimerQuery();
  const disclaimerText: string | null = disclaimerQuery.data?.IdpDisclaimerText ?? null;

  // DoseSpot's official IDP workflow requires the disclaimer text to be
  // displayed for the clinician to review before acceptance. If the API
  // returns no text we must block progression rather than have the prescriber
  // acknowledge a disclaimer they never saw.
  const hasDisclaimerText: boolean = disclaimerText !== null && disclaimerText.trim().length > 0;
  const isReady: boolean = hasReadDisclaimer && hasDisclaimerText;

  useEffect(() => {
    onReadyChange(isReady);
  }, [isReady, onReadyChange]);

  if (disclaimerQuery.isLoading) {
    return <WizardStepSkeleton />;
  }

  if (disclaimerQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load disclaimer</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>
              {disclaimerQuery.error?.message ??
                'The identity proofing disclaimer could not be fetched.'}
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void disclaimerQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (!hasDisclaimerText) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Disclaimer text is unavailable</AlertTitle>
        <AlertDescription>
          <div className="flex flex-col gap-2">
            <div>
              The identity proofing disclaimer text was not returned by DoseSpot, so we can&apos;t
              ask you to acknowledge it yet. Please retry, or contact support if this keeps
              happening.
            </div>
            <div>
              <Button
                type="button"
                variant="secondary"
                onClick={() => void disclaimerQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <form
      id={DS_STEP_FORM_ID}
      onSubmit={(e) => {
        e.preventDefault();
        if (!isReady || isSubmitting) {
          return;
        }
        onSubmit();
      }}
      className="flex h-full flex-col"
    >
      {submitError ? (
        <Alert variant="destructive" className="mb-4 shrink-0">
          <AlertTitle>Could not accept disclaimer</AlertTitle>
          <AlertDescription>{submitError.message}</AlertDescription>
        </Alert>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
          {disclaimerText}
        </p>
      </div>

      <label className="flex shrink-0 items-start gap-3 pt-4 text-sm">
        <Checkbox
          checked={hasReadDisclaimer}
          onCheckedChange={(checked) => setHasReadDisclaimer(Boolean(checked))}
          aria-describedby="disclaimer-acknowledgment"
        />
        <span id="disclaimer-acknowledgment">I have read and understand the disclaimer above.</span>
      </label>
    </form>
  );
}
